import { cellCenter } from '../grid.ts';
import { dist2, imax, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, SendaNpc, ShellRules, ShellState } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { stepToward } from './enemies.ts';
import { pulseLand } from './brasa.ts';
import { shootTick } from './workshop.ts';
import { SHELL_DRILLS, shellRight } from './tormo-layout.ts';

/**
 * The shell (docs/sendas/artifice.md, chapter 5, act 3, the test; the lesson of chapter 4, with one more turn). Dawn in
 * the workshop's yard; Tormo tied in its middle, the nightmare under the stone of his shoulders and back. Nadd's line of
 * drills has its own mill (`mill`, 3), the same rule as the mine: four drills, each with its clutch, force and arrow; belt
 * I turns drills 1 and 2 round, belt II drills 3 and 4. A drill in asks its force, cutting or not (turned against its
 * arrow it doesn't cut); more than the mill jams the shaft (every clutch out, `jamBp` of the hero's life, a cub free).
 *
 * The chest's drill goes on the same clutch and the same belt as one of them (`gang`), its arrow the other way: in with
 * it, it asks its force (`chestForce`) too; and whenever the gang's turns against its arrow, the chest's turns its own
 * way and cuts the chest: the heart's stone cracks, Tormo cries out and the clutch is thrown out (`cracks`: at the
 * third, lost). So the gang's shoulder is broken with the belt where the shoulder cuts and the chest turns back, and
 * the chest's ask counted.
 *
 * Each stone (left shoulder, right shoulder, back) falls cut its force (`need`, 2 or 3) for `cut`. Tormo writhes and
 * turns his other side to the line every `twist` (warned 3 s; his arms sweep `sweepR` round him, and the stones being cut
 * start over): which stone each drill reaches (`reach[side]`) is other. A stone down throws its shards (`shards`, warned
 * 3 s, round where it was) and lets out `cubs.out` ash cubs; more come now and then (`cubs`). A cub goes for the nearest
 * drill in: one that reaches it stops it `stall`, then goes for the hero. Won with the three stones down.
 *
 * Cost: four drills and three stones added up once a tick; a handful of cubs looking at four drills.
 */

const fresh = (r: ShellRules): ShellState => ({
  inn: r.drills.map(() => false),
  belts: [0, 0],
  stall: r.drills.map(() => 0),
  job: -1,
  held: 0,
  cut: [0, 0, 0],
  down: [-1, -1, -1],
  side: 0,
  twist: r.twist.first,
  twistAt: -1,
  twists: 0,
  cracks: 0,
  jams: 0,
  pulls: 0,
  spots: [],
  cubs: [],
  cub: r.cubs.first,
  shot: 0,
  pend: 0,
  end: -1,
});

const setOf = (r: ShellRules): { right0: boolean[] } => ({ right0: r.drills.map((d) => d.right0) });

/** Whether drill `d` turns its arrow's way now. */
export const shellTurnsRight = (r: ShellRules, w: ShellState, d: number): boolean => shellRight(setOf(r), w.belts, d);

/** Whether the chest's drill is in and cutting (its gang's in and turning backwards). */
export const shellChestCuts = (r: ShellRules, w: ShellState): boolean => !!w.inn[r.gang] && !shellTurnsRight(r, w, r.gang);

/** What the drills in ask of the mill (the chest's with its gang's). */
export const shellAsk = (r: ShellRules, w: ShellState): number => r.drills.reduce((a, x, d) => a + (w.inn[d] ? x.force : 0), 0) + (w.inn[r.gang] ? r.chestForce : 0);

/** The force on stone `k` now. */
export const shellForce = (s: BattleState, r: ShellRules, w: ShellState, k: number): number =>
  r.drills.reduce((a, x, d) => a + (w.inn[d] && s.tick >= (w.stall[d] as number) && (r.reach[w.side] as number[])[d] === k && shellTurnsRight(r, w, d) ? x.force : 0), 0);

/** A mark's cell: a drill's clutch spot (0–3), a lever (4–5). */
export const shellCell = (r: ShellRules, at: number): number => (at < SHELL_DRILLS ? (r.drills[at]?.spot as number) : (r.levers[at - SHELL_DRILLS] as number));

export const shellOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.shell;
  const w = s.senda?.shell;
  if (!r || !w || w.end >= 0 || at < 0 || at > SHELL_DRILLS + 1) return;
  w.job = at;
  w.held = 0;
  const c = cellCenter(s.map.radius, shellCell(r, at));
  orderMove(s, h, c.x, c.y);
  h.target = 0;
  h.forced = false;
};

/** A cub out of the stone or the cracks: for the drills. */
const cubOut = (s: BattleState, r: ShellRules, w: ShellState, x: number, y: number): void => {
  const e = spawnEnemy(s, 'goblin', 1, false, 0, x + rngRange(s.rng, -300, 300), y + rngRange(s.rng, -300, 300));
  const hp = s.rules?.enemyHpBp ? mulBp(e.maxHp, s.rules.enemyHpBp) : e.maxHp;
  e.hp = e.maxHp = imax(1, mulBp(hp, r.cubs.hpBp));
  w.cubs.push(e.id);
};

const jam = (s: BattleState, r: ShellRules, w: ShellState, cue: (c: string) => void): void => {
  w.jams++;
  for (let d = 0; d < w.inn.length; d++) w.inn[d] = false;
  const at = r.holes[rngInt(s.rng, r.holes.length)] as { x: number; y: number };
  ev(s, { t: 'senda', kind: 'jam', npc: '', x: at.x, y: at.y, n: w.jams });
  for (const h of s.heroes) if (h.downTicks === 0) damageHero(s, h, mulBp(h.stats.maxHp, r.jamBp), null, 'physical', true);
  cubOut(s, r, w, at.x, at.y);
  cue('jam');
};

/** The chest cut: the heart cracks, the gang's clutch is thrown out. */
const crack = (s: BattleState, r: ShellRules, w: ShellState, cue: (c: string) => void): void => {
  w.cracks++;
  w.inn[r.gang] = false;
  ev(s, { t: 'senda', kind: 'crack', npc: '', x: r.chestAt.x, y: r.chestAt.y, n: w.cracks });
  cue(`crack${w.cracks}`);
};

const heroWork = (s: BattleState, r: ShellRules, w: ShellState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0) return;
  const c = cellCenter(s.map.radius, shellCell(r, w.job));
  if (h.moving || !withinRange(h.x, h.y, c.x, c.y, r.near)) {
    w.held = 0;
    if (!h.moving) w.job = -1;
    return;
  }
  const lever = w.job >= SHELL_DRILLS;
  if (++w.held < (lever ? r.lever : r.clutch)) return;
  const at = w.job;
  w.job = -1;
  w.held = 0;
  if (lever) {
    const b = at - SHELL_DRILLS;
    w.belts[b] = 1 - (w.belts[b] as number);
    w.pulls++;
    ev(s, { t: 'senda', kind: 'belt', npc: '', x: c.x, y: c.y, n: b });
    if (shellChestCuts(r, w)) crack(s, r, w, cue);
    return;
  }
  const x = r.drills[at];
  if (!x) return;
  if (w.inn[at]) {
    w.inn[at] = false;
    ev(s, { t: 'senda', kind: 'clutchOut', npc: '', x: x.at.x, y: x.at.y, n: at });
    return;
  }
  w.inn[at] = true;
  ev(s, { t: 'senda', kind: 'clutchIn', npc: '', x: x.at.x, y: x.at.y, n: at });
  if (shellAsk(r, w) > r.mill) jam(s, r, w, cue);
  else if (shellChestCuts(r, w)) crack(s, r, w, cue);
};

/** Where stone `k` is now (turned with Tormo: side 1 mirrors it over his middle). */
export const shellStoneAt = (r: ShellRules, side: number, k: number): { x: number; y: number } => {
  const p = r.stones[k] as { x: number; y: number };
  return side === 0 ? p : { x: 2 * r.tormoAt.x - p.x, y: p.y };
};

const cutTick = (s: BattleState, r: ShellRules, w: ShellState, cue: (c: string) => void): void => {
  for (let k = 0; k < 3; k++) {
    if ((w.down[k] as number) >= 0) continue;
    if (shellForce(s, r, w, k) >= (r.need[k] as number)) w.cut[k] = (w.cut[k] as number) + 1;
    else w.cut[k] = 0;
    if ((w.cut[k] as number) < r.cut) continue;
    w.down[k] = s.tick;
    const n = w.down.filter((x) => x >= 0).length;
    const p = shellStoneAt(r, w.side, k);
    ev(s, { t: 'senda', kind: 'stoneDown', npc: '', x: p.x, y: p.y, n: k });
    cue(`stone${n}`);
    // Its shards, warned, over the whole yard (nowhere out of them: the stone bursts); its cubs.
    w.spots.push({ x: p.x, y: p.y, t: s.tick + r.shards.warn });
    ev(s, { t: 'senda', kind: 'pulseWarn', npc: '', x: p.x, y: p.y, n: r.shards.warn });
    for (let c = 0; c < r.cubs.out; c++) cubOut(s, r, w, p.x, p.y);
    if (n === 3) {
      w.end = s.tick;
      for (let d = 0; d < w.inn.length; d++) w.inn[d] = false;
    }
  }
};

const twist = (s: BattleState, r: ShellRules, w: ShellState, cue: (c: string) => void): void => {
  const T = r.twist;
  if (w.twistAt < 0 && s.waveT >= w.twist) {
    w.twistAt = s.tick + T.warn;
    ev(s, { t: 'senda', kind: 'twistWarn', npc: '', x: r.tormoAt.x, y: r.tormoAt.y, n: T.warn });
    cue('twist');
    return;
  }
  if (w.twistAt < 0 || s.tick < w.twistAt) return;
  w.twistAt = -1;
  w.twist = s.waveT + T.every - T.warn;
  w.side = 1 - w.side;
  w.twists++;
  for (let k = 0; k < 3; k++) w.cut[k] = 0;
  ev(s, { t: 'senda', kind: 'twist', npc: '', x: r.tormoAt.x, y: r.tormoAt.y, n: w.side });
  // The cable whips the whole yard (each stone still on him a share), his arms sweep round him.
  const on = w.down.filter((x) => x < 0).length + 1;
  for (const h of s.heroes) {
    if (h.downTicks > 0) continue;
    const near = withinRange(h.x, h.y, r.tormoAt.x, r.tormoAt.y, T.sweepR) ? T.heroBp : 0;
    damageHero(s, h, mulBp(h.stats.maxHp, near + T.whipBp * on), null, 'physical', true);
  }
  if (w.twists >= T.holds - 1) cue('strain');
};

/** A cub's move (from the enemies' tick): to the nearest drill in, stopping it; then it's a common one. */
export const shellHold = (s: BattleState, e: Enemy, px: number): boolean => {
  const r = s.rules?.senda?.shell;
  const w = s.senda?.shell;
  if (!r || !w || !w.cubs.includes(e.id)) return false;
  let best = -1;
  let bd = 0x7fffffff;
  r.drills.forEach((x, d) => {
    if (!w.inn[d] || s.tick < (w.stall[d] as number)) return;
    const dd = dist2(e.x, e.y, x.at.x, x.at.y);
    if (dd < bd) {
      bd = dd;
      best = d;
    }
  });
  if (best < 0) return false;
  const x = r.drills[best] as ShellRules['drills'][number];
  if (withinRange(e.x, e.y, x.at.x, x.at.y, r.cubs.stallR)) {
    w.stall[best] = s.tick + r.cubs.stall;
    w.cubs = w.cubs.filter((id) => id !== e.id);
    e.anim = 'attack';
    e.animT = 0;
    ev(s, { t: 'senda', kind: 'drillStall', npc: '', x: x.at.x, y: x.at.y, n: best });
    // (Said by the act's tick, not from the enemies' loop.)
    w.pend = 1;
    return true;
  }
  e.anim = 'walk';
  stepToward(e, x.at.x, x.at.y, px);
  return true;
};

export const shellTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.shell;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.shell ??= fresh(r));
  if (!s.started) return;
  if (w.pend) {
    w.pend = 0;
    cue('stall');
  }
  w.cubs = w.cubs.filter((id) => s.enemies.some((e) => e.id === id && e.hp > 0));
  if (w.end < 0) {
    heroWork(s, r, w, cue);
    twist(s, r, w, cue);
    cutTick(s, r, w, cue);
    if (s.waveT >= w.cub) {
      w.cub = s.waveT + r.cubs.every;
      const at = r.holes[rngInt(s.rng, r.holes.length)] as {
        x: number;
        y: number;
      };
      for (let k = 0; k < r.cubs.n; k++) cubOut(s, r, w, at.x, at.y);
    }
  }
  pulseLand(s, w.spots, { r: 30000, heroBp: r.shards.heroBp, blows: 1 });
  const d = st.npcs.find((n): n is SendaNpc => n.id === 'dunna' && n.hp > 0);
  if (d) {
    if (d.flinch > 0) d.flinch--;
    w.shot = shootTick(s, d, r.dunna, w.shot);
  }
};

/** The three stones. */
export const shellGoal = (s: BattleState): { done: number; need: number } => {
  const w = s.senda?.shell;
  return { done: w ? w.down.filter((x) => x >= 0).length : 0, need: 3 };
};

export const shellResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.shell;
  const w = s.senda?.shell;
  if (!r || !w) return null;
  if (w.cracks >= r.cracks || (w.end < 0 && w.twists > r.twist.holds)) return 'lost';
  return w.end >= 0 && s.tick >= w.end + 24 ? 'won' : null;
};
